import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Upload, Progress, message, Spin, Typography, Empty } from 'antd';
import { InboxOutlined, FileOutlined } from '@ant-design/icons';
import { gql, useLazyQuery } from '@apollo/client';
import axios from 'axios';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import { getApiHost } from '../../apolloClient';
import { useClientDocuments, Document } from '../../hooks/useClientDocuments';

const { Dragger } = Upload;
const { Title, Text } = Typography;
const apiUrl = getApiHost();

const DOWNLOAD_CLIENT_DOCUMENT = gql`
  query DownloadClientDocument($id: Float!) {
    document(id: $id) {
      downloadUrl
    }
  }
`;


// Document categories enum as per backend specification
export enum DocumentCategory {
  BANK_STATEMENTS = 'Bank Statements',
  COMPREHENSIVE_TRANSACTION_REPORTS = 'Comprehensive Transaction Reports',
  SILVERBILLS_MONTHLY_REPORTS = 'Monthly Reports',
  SILVERBILLS_YTD_REPORTS = 'SilverBills YTD Reports',
  SILVERBILLS_QUARTERLY_REPORTS = 'SilverBills Quarterly Reports',
  SILVERBILLS_ANNUAL_REPORTS = 'Annual Reports',
  SILVERBILLS_CUSTOM_DATE_REPORTS = 'Custom Date Reports',
  POA_DOCUMENT = 'POA Document',
  TRUST_STATEMENTS = 'Trust Statements',
  UNSORTED = 'Unsorted'
}

// Categories to send to backend (exact spelling from Acceptance Criteria)
const ALLOWED_BACKEND_CATEGORIES = [
  'Bank Statements',
  'Comprehensive Transaction Reports',
  'SilverBills Monthly Reports',
  'SilverBills YTD Reports',
  'SilverBills Quarterly Reports',
  'SilverBills Annual Reports',
  'SilverBills Custom Date Reports',
  'POA Document',
  'Trust Statements',
  'Unsorted'
];

// Map full backend category names to user-friendly labels
const AC_CATEGORY_RENAME_MAP: Record<string, string> = {
  'SilverBills Monthly Reports': DocumentCategory.SILVERBILLS_MONTHLY_REPORTS,
  'SilverBills Annual Reports': DocumentCategory.SILVERBILLS_ANNUAL_REPORTS,
  'SilverBills Custom Date Reports': DocumentCategory.SILVERBILLS_CUSTOM_DATE_REPORTS
};

// Mapping from backend category strings to display names
const CATEGORY_DISPLAY_MAP: Record<string, string> = {
  'BANK_STATEMENTS': DocumentCategory.BANK_STATEMENTS,
  'COMPREHENSIVE_TRANSACTION_REPORTS': DocumentCategory.COMPREHENSIVE_TRANSACTION_REPORTS,
  'SILVERBILLS_MONTHLY_REPORTS': DocumentCategory.SILVERBILLS_MONTHLY_REPORTS,
  'SILVERBILLS_YTD_REPORTS': DocumentCategory.SILVERBILLS_YTD_REPORTS,
  'SILVERBILLS_QUARTERLY_REPORTS': DocumentCategory.SILVERBILLS_QUARTERLY_REPORTS,
  'SILVERBILLS_ANNUAL_REPORTS': DocumentCategory.SILVERBILLS_ANNUAL_REPORTS,
  'SILVERBILLS_CUSTOM_DATE_REPORTS': DocumentCategory.SILVERBILLS_CUSTOM_DATE_REPORTS,
  'POA_DOCUMENT': DocumentCategory.POA_DOCUMENT,
  'TRUST_STATEMENTS': DocumentCategory.TRUST_STATEMENTS,
  'UNSORTED': DocumentCategory.UNSORTED
};

// Get display name for a backend category string
const getCategoryDisplayName = (backendCategory: string): string => {
  // If backend returns AC format, use it directly
  if (ALLOWED_BACKEND_CATEGORIES.includes(backendCategory)) {
    return AC_CATEGORY_RENAME_MAP[backendCategory] || backendCategory;
  }
  // Otherwise, map from old format to display format
  return CATEGORY_DISPLAY_MAP[backendCategory] || backendCategory;
};

// Note: Using Document interface from useClientDocuments hook

// Define the allowed categories for display (user-friendly names)
const ALLOWED_CATEGORIES = Object.values(DocumentCategory);

const Documents: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const userId = Number(localStorage.getItem('userId'));
  const { documents, loading, error, refetch } = useClientDocuments(userId, ALLOWED_BACKEND_CATEGORIES);

  // Debug logging
  React.useEffect(() => {
    console.log('[Documents] Component state:', {
      userId,
      documentsCount: documents.length,
      allowedCategories: ALLOWED_BACKEND_CATEGORIES,
      loading,
      error: error?.message
    });
    
    if (documents.length > 0) {
      console.log('[Documents] Sample documents:', documents.slice(0, 3));
      
      // Log category breakdown
      const categoryBreakdown = documents.reduce((acc, doc) => {
        acc[doc.category] = (acc[doc.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log('[Documents] Frontend category breakdown:', categoryBreakdown);
    }
  }, [documents, loading, error, userId]);

  const [downloadClientDocument] = useLazyQuery(DOWNLOAD_CLIENT_DOCUMENT);

  // Handle file upload using REST API
  const handleUpload = async () => {
    if (fileList.length > 0) {
      const formData = new FormData();
      
      // Ensure you're appending the actual file objects
      fileList.forEach(file => {
        if (file.originFileObj) {
          formData.append("files", file.originFileObj); // Use originFileObj to get the real file
        }
      });

      setUploading(true);
      setProgress(0); // Reset progress before starting upload

      try {
        const response = await axios.post(
          `${apiUrl}/users/upload-unprocessed-docs`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + localStorage.getItem("accessToken"), // Ensure the token is included
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setProgress(percent); // Update progress
              }
            },
          }
        );
        message.success(
          `Files uploaded successfully and will be processed accordingly.`,
          5
        );
        setFileList([]); // Clear the file list after successful upload
        // Reload documents after successful upload
        refetch();
      } catch (error) {
        console.error("Upload error:", error);
        message.error(`File upload failed.`);
      } finally {
        setUploading(false);
      }
    } else {
      message.warning("No files selected for upload.");
    }
  };

  const handleChange = (info: any) => {
    const newFileList = info.fileList.map((file: any) => ({
      ...file,
      status: "done",
    }));
    setFileList(newFileList);
  };

  // Handle document download using the DownloadClientDocument query
  const handleDownload = async (doc: any) => {
    setDownloadingId(doc.id);
    
    try {
      const { data } = await downloadClientDocument({
        variables: {
          id: doc.id,
        },
        fetchPolicy: "network-only",
      });

      if (data?.document?.downloadUrl) {
        // Open the document in a new tab
        window.open(data.document.downloadUrl, '_blank');
      } else {
        message.error("Unable to get download URL");
      }
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download document");
    } finally {
      setDownloadingId(null);
    }
  };

  // Transform backend category strings to display names (backend already filtered by categories)
  const transformedDocuments = documents.map(doc => ({
    ...doc,
    // Transform the backend category string to display name
    category: getCategoryDisplayName(doc.category) as DocumentCategory
  }));

  // Group documents by display category
  const groupedDocuments = transformedDocuments.reduce((acc, doc) => {
    const displayCategory = doc.category;
    if (!acc[displayCategory]) {
      acc[displayCategory] = [];
    }
    acc[displayCategory].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);



  // Create flat table data with category grouping
  const createTableData = () => {
    const tableData: any[] = [];
    
    ALLOWED_CATEGORIES.forEach(category => {
      const categoryDocs = groupedDocuments[category];
      if (categoryDocs && categoryDocs.length > 0) {
        // Add category header row
        tableData.push({
          key: `category-${category}`,
          isCategory: true,
          category: category,
          fileName: '',
          uploadDate: '',
          documentType: ''
        });
        
        // Add document rows
        categoryDocs.forEach(doc => {
          tableData.push({
            ...doc,
            key: `doc-${doc.id}`,
            isCategory: false,
            category: ''
          });
        });
      }
    });
    
    return tableData;
  };

  // Table columns for unified view
  const columns: ColumnsType<any> = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      render: (text, record) => {
        if (record.isCategory) {
          return (
            <div style={{ 
              fontWeight: 'bold', 
              fontSize: '14px',
              color: '#1890ff',
              padding: '8px 0'
            }}>
              {record.category}
            </div>
          );
        }
        return (
          <div style={{ paddingLeft: '20px' }}>
            <FileOutlined style={{ marginRight: 8, color: '#666' }} />
            <Button 
              type="link" 
              onClick={() => handleDownload(record)}
              loading={downloadingId === record.id}
              style={{ padding: 0, height: 'auto', textAlign: 'left' }}
            >
              {text}
            </Button>
          </div>
        );
      },
    },
    {
      title: 'Sort Date',
      dataIndex: 'sortDate',
      key: 'sortDate',
      width: 150,
      render: (date, record) => {
        if (record.isCategory) return '';
        // Use sortDate if available, otherwise fall back to uploadDate
        const dateToShow = record.sortDate || record.uploadDate;
        return dateToShow ? new Date(dateToShow).toLocaleDateString() : '';
      },
    },
  ];

  // Documents view content
  const documentsContent = (
    <div style={{ padding: 20 }}>
      <Title level={4} style={{ margin: 0, marginBottom: 8 }}>My Documents</Title>
      <Text type="secondary">
        Here are your documents organized by category. To upload new documents, include all pages of each document as a single file. You may upload multiple files at a time. Please allow up to one business day for us to process your documents.
      </Text>

      {/* Upload Section */}
      <div style={{ 
        marginTop: 24, 
        marginBottom: 32, 
        padding: 20, 
        backgroundColor: '#fafafa', 
        borderRadius: 8,
        border: '1px solid #d9d9d9'
      }}>
        <Title level={5} style={{ margin: 0, marginBottom: 16 }}>Upload New Documents</Title>
        
        <Dragger
          accept="application/pdf"
          listType="picture-card"
          multiple={true}
          beforeUpload={() => false}
          onChange={handleChange}
          fileList={fileList}
          showUploadList={{ 
            showRemoveIcon: true,
            showPreviewIcon: false,
            showDownloadIcon: false
          }}
          style={{ marginBottom: 16 }}
        >
          <div>
            <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            <div style={{ marginTop: 8, fontSize: '16px' }}>Click or drag to upload PDFs</div>
            <div style={{ marginTop: 4, fontSize: '14px', color: '#666' }}>
              {fileList.length > 0 ? `${fileList.length} file(s) selected` : 'Select multiple files at once'}
            </div>
          </div>
        </Dragger>

        {uploading && (
          <Progress 
            percent={progress} 
            status="active" 
            style={{ marginBottom: 16 }}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
          />
        )}

        <Button
          type="primary"
          onClick={handleUpload}
          size="large"
          disabled={fileList.length === 0 || uploading}
          loading={uploading}
          style={{ width: '100%' }}
        >
          {uploading ? `Uploading... ${progress}%` : `Upload ${fileList.length > 0 ? fileList.length : ''} Selected File${fileList.length !== 1 ? 's' : ''}`}
        </Button>
      </div>

      {/* Documents List Section */}
      <div>
        <Title level={5} style={{ margin: 0, marginBottom: 16 }}>Your Documents</Title>
        
        <Spin spinning={loading}>
          {error && (
            <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#fff2f0', borderRadius: 8 }}>
              <Text type="danger">
                Error loading documents: {error.message}
              </Text>
            </div>
          )}
          
          {Object.keys(groupedDocuments).length === 0 ? (
            <Empty 
              description="No documents found"
              style={{ margin: '40px 0' }}
            />
          ) : (
            <div>
              <style>
                {`
                  .document-row-even {
                    background-color: #ffffff !important;
                  }
                  .document-row-odd {
                    background-color: #f5f5f5 !important;
                  }
                  .category-row {
                    background-color: #fafafa !important;
                    font-weight: bold;
                  }
                `}
              </style>
              <Table
                columns={columns}
                dataSource={createTableData()}
                pagination={false}
                rowKey="key"
                size="small"
                showHeader={true}
                bordered={false}
                rowClassName={(record, index) => {
                  if (record.isCategory) {
                    return 'category-row';
                  }
                  return index % 2 === 0 ? 'document-row-even' : 'document-row-odd';
                }}
              />
            </div>
          )}
        </Spin>
      </div>
    </div>
  );

  return (
    <>
      <h1>Documents</h1>
      {documentsContent}
    </>
  );
};

export default Documents;
